import React from "react";
import { ChatAlt2Icon } from "@heroicons/react/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp, faTwitter, faLinkedin, faGithub } from "@fortawesome/free-brands-svg-icons";

const WHATSAPP_NUMBER = "+2348030914767";

const quickMessages = [
  { label: "👋 Say hello", text: "Hi Emmanuel! I'd love to connect." },
  { label: "💼 Hire me", text: "Hi Emmanuel, I have a project opportunity I'd like to discuss with you." },
  { label: "🤝 Collaborate", text: "Hi Emmanuel, I'm interested in collaborating on a project together." },
  { label: "❓ Ask a question", text: "Hi Emmanuel, I have a question for you." },
];

const socialLinks = [
  { href: "https://twitter.com/codehouze", icon: faTwitter, label: "Twitter", color: "hover:text-blue-400" },
  { href: "https://linkedin.com/in/codehouze", icon: faLinkedin, label: "LinkedIn", color: "hover:text-blue-500" },
  { href: "https://www.github.com/codehouze/", icon: faGithub, label: "GitHub", color: "hover:text-white" },
];

const buildWhatsAppUrl = (text) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

const Contact = () => {
  return (
    <section id="contact" className="py-20 bg-gray-900">
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex flex-col items-center mb-16">
          <ChatAlt2Icon className="w-8 h-8 text-emerald-400 mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Let's Talk</h2>
          <div className="section-divider" />
        </div>

        <div className="flex flex-col md:flex-row gap-10 items-start">
          {/* Left — intro */}
          <div className="flex-1">
            <h3 className="text-3xl font-bold text-white mb-4">Let's Build Together! 🚀</h3>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              Whether you have a project in mind, a question, or just want to connect. I'm a
              message away. Hit one of the quick starters below and I'll get back to you on WhatsApp.
            </p>

            <div className="flex items-center gap-5">
              {socialLinks.map(({ href, icon, label, color }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className={`text-gray-500 ${color} transition-colors duration-200`}
                >
                  <FontAwesomeIcon icon={icon} className="text-2xl" />
                </a>
              ))}
            </div>
          </div>

          {/* Right — WhatsApp chatbot card */}
          <div className="flex-1 w-full">
            <div className="bg-gray-800/60 border border-gray-700/50 rounded-2xl overflow-hidden">
              {/* Chat header */}
              <div className="flex items-center gap-3 bg-[#075E54] px-5 py-4">
                <div className="w-10 h-10 rounded-full bg-emerald-400/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faWhatsapp} className="text-white text-xl" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Emmanuel Maxwell</p>
                  <p className="text-emerald-200 text-xs">Typically replies instantly</p>
                </div>
                <span className="ml-auto w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>

              {/* Chat body */}
              <div className="p-5 space-y-3">
                <div className="bg-gray-700/60 rounded-xl rounded-tl-none px-4 py-3 max-w-xs">
                  <p className="text-gray-200 text-sm">
                    👋 Hey! Pick a quick message below or tap the button to start a custom chat.
                  </p>
                  <p className="text-gray-500 text-xs mt-1 text-right">now</p>
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  {quickMessages.map(({ label, text }) => (
                    <a
                      key={label}
                      href={buildWhatsAppUrl(text)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm bg-gray-700 hover:bg-[#075E54] border border-gray-600 hover:border-[#075E54] text-gray-300 hover:text-white px-3 py-1.5 rounded-full transition-all duration-200"
                    >
                      {label}
                    </a>
                  ))}
                </div>
              </div>

              {/* CTA button */}
              <div className="px-5 pb-5">
                <a
                  href={buildWhatsAppUrl("Hi Emmanuel, I'd like to get in touch!")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 w-full bg-[#25D366] hover:bg-[#20b858] text-white font-semibold py-3 rounded-xl transition-colors duration-200"
                >
                  <FontAwesomeIcon icon={faWhatsapp} className="text-xl" />
                  Open WhatsApp Chat
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
